/**
 * Iter 95 — Formatage monétaire régional (miroir de backend/region/config.py).
 *   CH → CHF 18'450 · FR → 18 450 € · DE/IT/ES/PT → 18.450 € · US → $18,450
 *
 * Deux familles de fonctions :
 *  - formatMoney(amount, region)  → devise du TENANT (région détectée/compte)
 *  - formatCHF(amount, locale)    → montants EXPLICITEMENT en CHF (ledgers
 *    affiliés, données historiques) — la devise ISO d'origine est conservée.
 */

export const DEFAULT_REGION = {
  country: 'CH', currency: 'CHF', checkout_currency: 'CHF', locale: 'fr',
};

const REGION_CACHE_KEY = 'cop_region_ctx';

export function getCachedRegion() {
  try {
    const raw = window.localStorage.getItem(REGION_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function cacheRegion(region) {
  try {
    window.localStorage.setItem(REGION_CACHE_KEY, JSON.stringify(region));
  } catch {
    /* ignore */
  }
}

const CURRENCY_FORMATS = {
  CHF: { symbol: 'CHF', before: true, sep: ' ', group: "'" },
  EUR: { symbol: '€', before: false, sep: ' ', group: '.' },
  USD: { symbol: '$', before: true, sep: '', group: ',' },
  GBP: { symbol: '£', before: true, sep: '', group: ',' },
  CAD: { symbol: '$', before: true, sep: '', group: ',' },
  AUD: { symbol: '$', before: true, sep: '', group: ',' },
  NZD: { symbol: '$', before: true, sep: '', group: ',' },
  JPY: { symbol: '¥', before: true, sep: '', group: ',' },
  SGD: { symbol: '$', before: true, sep: '', group: ',' },
  NOK: { symbol: 'kr', before: false, sep: ' ', group: ' ' },
  SEK: { symbol: 'kr', before: false, sep: ' ', group: ' ' },
  DKK: { symbol: 'kr', before: false, sep: ' ', group: '.' },
  PLN: { symbol: 'zł', before: false, sep: ' ', group: ' ' },
  CZK: { symbol: 'Kč', before: false, sep: ' ', group: ' ' },
};

// convention de groupement EUR par pays (FR : 18 450 € — DE/IT/ES/PT : 18.450 €)
const EUR_GROUP_BY_COUNTRY = {
  FR: ' ', BE: '.', LU: '.', DE: '.', AT: '.', IT: '.', ES: '.', PT: '.',
  NL: '.', IE: ',', FI: ' ', GR: '.', SK: ' ', SI: '.', EE: ' ', LV: ' ',
  LT: ' ', CY: '.', MT: ',', HR: '.',
};

function group(n, sep) {
  return String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

/** Formate un montant dans la devise du tenant (région du compte/visiteur). */
export function formatMoney(amount, region) {
  const r = (region && typeof region === 'object')
    ? region : (getCachedRegion() || DEFAULT_REGION);
  const currency = (r.currency || 'CHF').toUpperCase();
  const country = (r.country || 'CH').toUpperCase();
  const fmt = { ...(CURRENCY_FORMATS[currency] || CURRENCY_FORMATS.EUR) };
  if (currency === 'EUR') fmt.group = EUR_GROUP_BY_COUNTRY[country] || '.';
  const n = Math.round(Number(amount) || 0);
  const digits = group(n, fmt.group);
  const sign = n < 0 ? '-' : '';
  return fmt.before
    ? `${fmt.symbol}${fmt.sep}${sign}${digits}`
    : `${sign}${digits}${fmt.sep}${fmt.symbol}`;
}

/** Formate un objet monétaire structuré { amount, currency } — devise ISO conservée. */
export function formatAmount(money, country) {
  if (!money) return '';
  return formatMoney(money.amount, {
    currency: money.currency, country: country || 'CH',
  });
}

/**
 * Montants EXPLICITEMENT libellés en CHF (ledger affilié, factures CHF
 * historiques). Ne jamais utiliser pour un montant dans la devise du tenant.
 */
export function formatCHF(amount, locale = 'en') {
  if (amount === null || amount === undefined || isNaN(amount)) return 'CHF 0';
  const rounded = Math.round(Number(amount));
  const sep = locale === 'en' ? ',' : "'";
  const sign = rounded < 0 ? '-' : '';
  return `${sign}CHF ${group(rounded, sep)}`;
}

export const fmt = formatCHF;

/** Compat héritée (modules facturation legacy) — devise ISO explicite.
 *
 * Iter 126 — formateur CENTRAL du dashboard :
 *   formatCurrency(amount, sourceCurrency, displayCurrency, currencyContext)
 *   - sans displayCurrency (ou 'source' / identique) → montant formaté dans
 *     sa devise SOURCE, jamais ré-étiqueté ;
 *   - avec displayCurrency ≠ source → conversion UNIQUEMENT si le
 *     currencyContext (backend : fx_rate BCE daté, source/display) couvre
 *     exactement cette paire ; sinon repli sur la devise source.
 *   - format du montant converti selon la langue UI (fr 7 058 € · de/it/es/pt
 *     7.058 € · en €7,058 · CHF → CHF 7'058).
 */
const EUR_FORMAT_BY_LOCALE = {
  fr: { symbol: '€', before: false, sep: ' ', group: ' ' },
  de: { symbol: '€', before: false, sep: ' ', group: '.' },
  it: { symbol: '€', before: false, sep: ' ', group: '.' },
  es: { symbol: '€', before: false, sep: ' ', group: '.' },
  pt: { symbol: '€', before: false, sep: ' ', group: '.' },
  en: { symbol: '€', before: true, sep: '', group: ',' },
};

const DISPLAY_PREF_KEY = 'cop_display_ccy';
const DISPLAY_CHOICES = ['source', 'CHF', 'EUR'];

export function normalizeDisplayChoice(value) {
  if (value == null) return null;
  const v = String(value).trim();
  if (!v) return null;
  if (v.toLowerCase() === 'source') return 'source';
  const up = v.toUpperCase();
  return DISPLAY_CHOICES.includes(up) ? up : null;
}

export function getCachedDisplayPreference() {
  try { return normalizeDisplayChoice(window.localStorage.getItem(DISPLAY_PREF_KEY)); } catch { return null; }
}

export function cacheDisplayPreference(choice) {
  try {
    const c = normalizeDisplayChoice(choice);
    if (c) window.localStorage.setItem(DISPLAY_PREF_KEY, c);
    else window.localStorage.removeItem(DISPLAY_PREF_KEY);
  } catch { /* ignore */ }
}

// Snapshot d'affichage posé par DisplayCurrencyProvider (contexte financier du
// COMPTE, jamais la région navigateur) — lu par les helpers non-hook (chf(), fmt()).
let DISPLAY_SNAPSHOT = {
  ready: false, sourceCurrency: null, sourceCountry: null, requested: 'source',
  displayCurrency: null, fx_rate: null, fx_as_of: null, fx_source: null, locale: 'fr',
};

export function setDisplaySnapshot(snap) {
  DISPLAY_SNAPSHOT = { ...DISPLAY_SNAPSHOT, ...(snap || {}) };
}

export function getDisplaySnapshot() {
  return DISPLAY_SNAPSHOT;
}

function render(fmt, amount) {
  const num = Number(amount);
  if (amount === '' || amount == null) return render(fmt, 0);
  if (!Number.isFinite(num)) return '—';
  const n = Math.round(num);
  const digits = group(n, fmt.group);
  const sign = n < 0 ? '-' : '';
  return fmt.before ? `${fmt.symbol}${fmt.sep}${sign}${digits}` : `${sign}${digits}${fmt.sep}${fmt.symbol}`;
}

function formatInCurrencyForLocale(amount, currency, locale) {
  const code = (currency || '').toUpperCase();
  if (code === 'EUR') {
    const loc = String(locale || 'en').slice(0, 2).toLowerCase();
    return render(EUR_FORMAT_BY_LOCALE[loc] || EUR_FORMAT_BY_LOCALE.en, amount);
  }
  return render(CURRENCY_FORMATS[code] || CURRENCY_FORMATS.EUR, amount);
}

function sourceCountryFor(currency, ctx) {
  if (ctx && ctx.sourceCountry) return ctx.sourceCountry;
  if (currency === 'CHF') return 'CH';
  const cached = getCachedRegion();
  if (cached && (cached.currency || '').toUpperCase() === currency && cached.country) return cached.country;
  return currency === 'EUR' ? 'DE' : 'US';
}

export function formatCurrency(amount, sourceCurrency = 'CHF', displayCurrency = null, currencyContext = null) {
  const src = (typeof sourceCurrency === 'string' && sourceCurrency ? sourceCurrency : 'CHF').toUpperCase();
  const disp = normalizeDisplayChoice(displayCurrency);
  const ctx = currencyContext || null;
  const num = Number(amount);
  if (amount !== '' && amount != null && !Number.isFinite(num)) return '—';
  const inSource = () => formatMoney(amount == null || amount === '' ? 0 : num, {
    currency: src, country: sourceCountryFor(src, ctx),
  });
  if (!disp || disp === 'source' || disp === src) return inSource();
  const ctxSrc = (ctx?.source_currency || ctx?.sourceCurrency || '').toUpperCase();
  const ctxDisp = (ctx?.display_currency || ctx?.displayCurrency || '').toUpperCase();
  const rate = Number(ctx?.fx_rate);
  if (!ctx || ctxSrc !== src || ctxDisp !== disp || !Number.isFinite(rate) || rate <= 0) {
    return inSource(); // conversion non fiable → jamais de ré-étiquetage
  }
  const converted = (amount == null || amount === '' ? 0 : num) * rate;
  return formatInCurrencyForLocale(converted, disp, ctx.locale || DISPLAY_SNAPSHOT.locale);
}

/** Montant analytique du dashboard (devise source du compte → devise d'affichage choisie). */
export function formatDisplay(amount, sourceCurrency) {
  const s = DISPLAY_SNAPSHOT;
  const src = (sourceCurrency || s.sourceCurrency || getCachedRegion()?.currency || 'CHF').toUpperCase();
  if (!s.ready || !s.displayCurrency) {
    return formatMoney(amount == null || amount === '' ? 0 : Number(amount), {
      currency: src, country: sourceCountryFor(src, s),
    });
  }
  return formatCurrency(amount, src, s.displayCurrency, {
    source_currency: s.sourceCurrency, display_currency: s.displayCurrency,
    fx_rate: s.fx_rate, locale: s.locale, sourceCountry: s.sourceCountry,
  });
}

/** Devise dans laquelle l'utilisateur SAISIT ses données (ticket moyen…) : toujours la source. */
export function sourceCurrencyCode() {
  return (DISPLAY_SNAPSHOT.sourceCurrency || getCachedRegion()?.currency || 'CHF').toUpperCase();
}
